import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import {
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box } from '~/components/elements/Box'
import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { VStack } from '~/components/elements/VStack'
import { ensureExists, mapItemsToObj } from '~/utils'

import { dialogInfoAtom, linkCustomerAttributesInfoAtom } from '../../atoms'

import { CustomerAttributeInput } from './CustomerAttributeInput'
import { type InputValues, resolver } from './utils'

export type LinkCustomerAttributesDialogProps = {
  onExecute: (
    index: number,
    customerAttributes: InputValues['customerAttributes'],
  ) => void
}

export const LinkCustomerAttributesDialog = ({
  onExecute,
}: LinkCustomerAttributesDialogProps) => {
  const { t } = useTranslation()
  const linkCustomerAttributesInfo = useAtomValue(
    linkCustomerAttributesInfoAtom,
  )
  const { customerAttributeGroup, index } = ensureExists(
    linkCustomerAttributesInfo,
    t('assertion_failed'),
  )
  const methods = useForm<InputValues>({
    defaultValues: {
      customerAttributes: customerAttributeGroup.customerAttributes,
    },
    resolver,
  })
  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = methods
  const { append, fields, remove } = useFieldArray({
    control,
    keyName: 'fieldId',
    name: 'customerAttributes',
  })

  const dialogInfo = useAtomValue(dialogInfoAtom)
  const surveyGroup = ensureExists(
    dialogInfo?.surveyGroup,
    t('assertion_failed'),
  )
  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    append({
      name: '',
      attachedCustomerAttributeMap: mapItemsToObj(
        surveyGroup.surveys,
        () => null,
      ),
    })
  }, [append, surveyGroup.surveys])

  const resetLinkCustomerAttributesInfo = useResetAtom(
    linkCustomerAttributesInfoAtom,
  )
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ customerAttributes }) => {
      if (!isDirty) {
        resetLinkCustomerAttributesInfo()

        return
      }

      onExecute(index, customerAttributes)
      resetLinkCustomerAttributesInfo()
    },
    [index, isDirty, onExecute, resetLinkCustomerAttributesInfo],
  )

  return (
    <FormProvider {...methods}>
      <ExecuteCancelDialog
        executeButtonText={t('confirm_3')}
        mode="settings"
        title={t(
          'customized_segments_group_customerattributegroup_name_link_customized_segments',
          { customerAttributeGroup },
        )}
        width="100%"
        onCancel={resetLinkCustomerAttributesInfo}
        onExecute={handleSubmit(handleValid)}
      >
        <VStack spacing={0}>
          <Box padding={2}>
            <StyledButton icon="add" onClick={handleAddButtonClick}>
              {t('add_customized_segments')}
            </StyledButton>
          </Box>

          {fields.map((field, index) => (
            <CustomerAttributeInput
              key={field.fieldId}
              index={index}
              onDelete={remove}
            />
          ))}
        </VStack>
      </ExecuteCancelDialog>
    </FormProvider>
  )
}
